// RECALL-RULES / RECALL-PROCEDURES / RECALL-UPKEEP twin (services/api routes memory.ts and
// business.ts): standing rules, the dream and hot copy settings, and a playbook's procedure runs,
// in memory. It starts empty: no rule, no run. The server's rules it keeps: an owner or admin
// (`manager`) states a rule and it binds at once; anyone else's is `proposed` with a `memory_rule`
// approval (filed with the agents twin), and it binds once that approval is approved or edited
// (title and text from the approved payload) or is dismissed when it is rejected; only managers
// lift, and only an active rule; `de_id` lists the company's rules and that DE's own, plus the
// board automations it owns. Recall is never reached: a bound rule reads `synced` while the
// twin's Recall is connected, `not_offered` otherwise.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import {
  type Ctx,
  type Handler,
  json,
  notFound,
  type Principal,
  problem,
  validation,
} from "./fake";
import { NO_EXECUTION } from "./fake-approval-execution";
import type { FakeDe } from "./fake-data";
import { uid } from "./fake-data";

type Rule = OperationOutput<"memoryRules.list">["data"][number];
type AutomationRef = OperationOutput<"memoryRules.list">["automation_rules"][number];
type Upkeep = OperationOutput<"recall.upkeep">;
type Procedure = OperationOutput<"pages.playbookProcedure">;
type RunView = Procedure["recent"][number];

type Approval = OperationOutput<"my.approvals">["data"][number];

/** A run a test or the dev build records (what `memory.agentProcedureRun` would keep). */
export type FakeProcedureRun = {
  page_id: string;
  de_membership_id: string;
  task: string;
  result: RunView["result"];
  steps?: RunView["steps"];
  note?: string | null;
  minutes_ago?: number;
};

/** The routes behind the `memory` add-on (the dispatcher gates them). */
export const FAKE_RULES_MEMORY_ROUTES: ReadonlySet<RouteId> = new Set<RouteId>([
  "memoryRules.list",
  "memoryRules.create",
  "memoryRules.lift",
  "recall.upkeep",
]);

const NOT_OFFERED = "This Recall has no door CompanyOS may use for it yet.";

export function createRulesFake(deps: {
  now: () => Date;
  desOf: (companyId: string) => FakeDe[];
  /** Whether the twin's Recall is connected. */
  recallLive: boolean;
  /** The agents twin's approvals of a company (live: a filed approval is pushed onto it). */
  approvalsOf: (companyId: string) => readonly Approval[];
  /** A composed handler's JSON answer, as the caller (undefined: refused). */
  call: <T>(id: RouteId, ctx: Ctx) => T | undefined;
  /** A member's display name. */
  nameOf: (companyId: string, membershipId: string) => string | null;
}): {
  handlers: Partial<Record<RouteId, Handler>>;
  addProcedureRun: (companyId: string, run: FakeProcedureRun) => void;
} {
  let seq = 0;
  const next = () => uid(0x7e10, ++seq);
  const stamp = (minutesAgo = 0) =>
    new Date(deps.now().getTime() - minutesAgo * 60_000).toISOString();
  const rules = new Map<string, Rule[]>();
  const runs = new Map<string, RunView[]>();
  const rulesOf = (co: string) => {
    let list = rules.get(co);
    if (!list) {
      list = [];
      rules.set(co, list);
    }
    return list;
  };
  const membershipOf = (p: Principal) =>
    p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id ?? null;
  const member = (p: Principal | null): p is Principal =>
    p?.roles.some((r) => r !== "client_viewer") === true;
  const person = (co: string, id: string | null): Rule["proposed_by"] => {
    if (!id) return null;
    const de = deps.desOf(co).find((d) => d.membership_id === id);
    if (de) return { membership_id: id, name: de.display_name, kind: "digital_employee" };
    return { membership_id: id, name: deps.nameOf(co, id) ?? "A person", kind: "person" };
  };
  const bound = () =>
    deps.recallLive
      ? { recall_state: "synced" as const, recall_detail: null }
      : { recall_state: "not_offered" as const, recall_detail: NOT_OFFERED };

  /** A proposal follows its approval: approved/edited binds it, rejected/expired dismisses it. */
  function settle(co: string, r: Rule) {
    if (r.status !== "proposed" || !r.approval_id) return;
    const a = deps.approvalsOf(co).find((x) => x.id === r.approval_id);
    if (!a || a.state === "pending") return;
    const by = person(co, a.decided_by_membership_id);
    if (a.state === "approved" || a.state === "edited") {
      const c = a.approved_payload?.change ?? {};
      if (typeof c.title === "string" && c.title.trim()) r.title = c.title.trim();
      if (typeof c.text === "string" && c.text.trim()) r.text = c.text.trim();
      Object.assign(r, { status: "active", decided_by: by, decided_at: a.decided_at }, bound());
    } else {
      Object.assign(r, { status: "dismissed", decided_by: by, decided_at: a.decided_at });
    }
  }

  const sub = (ctx: Ctx, path: string, params: Record<string, string> = {}): Ctx => ({
    ...ctx,
    params: { co: ctx.params.co ?? "", ...params },
    url: new URL(`http://fake.local${path}${path.includes("?") ? "&" : "?"}limit=200`),
    body: undefined,
  });

  /** B1-DE-RULES: the board automations the DE owns (on boards the caller can view). */
  function automationsOf(ctx: Ctx, deId: string): AutomationRef[] {
    type Row = {
      id: string;
      name: string;
      board_id: string | null;
      board_name: string | null;
      enabled: boolean;
      owner_membership_id: string | null;
    };
    const list = deps.call<{ data: Row[] }>("automations.list", sub(ctx, "/automations"));
    return (list?.data ?? [])
      .filter((r) => r.owner_membership_id === deId && r.board_id)
      .map((r) => ({
        id: r.id,
        name: r.name,
        board_id: r.board_id as string,
        board_name: r.board_name ?? "Board",
        enabled: r.enabled,
      }));
  }

  const str = (v: unknown, max: number): string | null | false => {
    if (v === undefined) return null;
    if (typeof v !== "string" || !v.trim() || v.trim().length > max) return false;
    return v.trim();
  };

  const handlers: Partial<Record<RouteId, Handler>> = {
    "memoryRules.list": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
      const deId = ctx.url.searchParams.get("de_id");
      const status = ctx.url.searchParams.get("status") ?? "current";
      if (status !== "current" && status !== "all") return validation("status: current or all");
      if (deId && !deps.desOf(p.company.id).some((d) => d.membership_id === deId))
        return validation("de_id: not a DE of this company");
      const list = rulesOf(p.company.id);
      for (const r of list) settle(p.company.id, r);
      const data = list
        .filter((r) => status === "all" || r.status === "active" || r.status === "proposed")
        .filter((r) => !deId || r.scope === "company" || r.de?.membership_id === deId)
        .slice()
        .sort((a, b) => b.created_at.localeCompare(a.created_at));
      return json({
        data,
        automation_rules: deId ? automationsOf(ctx, deId) : [],
        can_manage: p.manager,
      });
    },
    "memoryRules.create": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
      const b = (ctx.body ?? {}) as Record<string, unknown>;
      if (b.scope !== "company" && b.scope !== "de") return validation("scope: company or de");
      if (!["setup", "de_page", "chat"].includes(String(b.source)))
        return validation("source: setup, de_page or chat");
      const text = str(b.text, 1000);
      if (!text) return validation("text: 1 to 1000 characters");
      const title = str(b.title, 120);
      if (title === false) return validation("title: 1 to 120 characters");
      const why = str(b.why, 1000);
      if (why === false) return validation("why: 1 to 1000 characters");
      if (b.source === "chat" && typeof b.message_id !== "string")
        return validation("message_id: the message the rule was made from");
      let de: Rule["de"] = null;
      if (b.scope === "de") {
        const found = deps.desOf(p.company.id).find((d) => d.membership_id === b.de_id);
        if (!found) return validation("de_id: a DE of this company");
        de = {
          membership_id: found.membership_id,
          name: found.display_name,
          kind: "digital_employee",
        };
      } else if (b.de_id !== undefined) return validation("de_id: only for a DE rule");
      const finalTitle = title ?? (text.length > 60 ? `${text.slice(0, 57)}…` : text);
      const list = rulesOf(p.company.id);
      if (
        list.some(
          (r) =>
            (r.status === "active" || r.status === "proposed") &&
            r.scope === b.scope &&
            (r.de?.membership_id ?? null) === (de?.membership_id ?? null) &&
            r.text === text,
        )
      )
        return problem(409, "conflict", "Conflict", "The same rule is already in place");
      const me = membershipOf(p);
      const id = next();
      const rule: Rule = {
        id,
        scope: b.scope,
        de,
        title: finalTitle,
        text,
        why,
        source: b.source as Rule["source"],
        status: p.manager ? "active" : "proposed",
        proposed_by: person(p.company.id, me),
        approval_id: null,
        decided_by: p.manager ? person(p.company.id, me) : null,
        decided_at: p.manager ? stamp() : null,
        lifted_at: null,
        ...(p.manager ? bound() : { recall_state: "pending" as const, recall_detail: null }),
        created_at: stamp(),
      };
      if (!p.manager) {
        const t = stamp();
        const approver = deps.desOf(p.company.id)[0]?.supervisor.seat_id ?? next();
        const a: Approval = {
          company_id: p.company.id,
          id: next(),
          kind: "memory_rule",
          subject_kind: "memory_rule",
          subject_id: id,
          requested_by_seat_id: null,
          requested_by_membership_id: me,
          approver_seat_id: approver,
          run_id: null,
          proposal: {
            title: `Standing rule: ${finalTitle}`,
            ...(why ? { summary: why } : {}),
            change: {
              rule_id: id,
              scope: rule.scope,
              de_membership_id: de?.membership_id ?? null,
              de_name: de?.name ?? null,
              title: finalTitle,
              text,
              why,
              source: rule.source,
            },
          },
          state: "pending",
          approved_payload: null,
          decided_at: null,
          decided_by_membership_id: null,
          reason: null,
          thread_id: null,
          expires_at: null,
          executed_at: null,
          ...NO_EXECUTION,
          actor_kind: "person",
          actor_membership_id: me,
          created_at: t,
          updated_at: t,
        };
        (deps.approvalsOf(p.company.id) as Approval[]).unshift(a);
        rule.approval_id = a.id;
      }
      list.push(rule);
      return json(rule, 201);
    },
    "memoryRules.lift": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      if (!p.manager) return problem(403, "forbidden", "Forbidden", "Managers only");
      const r = rulesOf(p.company.id).find((x) => x.id === ctx.params.id);
      if (!r) return notFound();
      settle(p.company.id, r);
      if (r.status !== "active") return problem(409, "conflict", "Conflict", "Only an active rule");
      const reason = (ctx.body as { reason?: unknown } | undefined)?.reason;
      if (reason !== undefined && (typeof reason !== "string" || !reason.trim()))
        return validation("reason: 1 to 500 characters");
      Object.assign(r, { status: "lifted", lifted_at: stamp() });
      return json(r);
    },
    "recall.upkeep": ({ principal: p }) => {
      if (!p) return notFound();
      if (!p.manager) return problem(403, "forbidden", "Forbidden", "Managers only");
      const m = p.company.settings.memory;
      const d = m?.dreams;
      const enabled = d?.enabled === true;
      const body: Upkeep = {
        dreams: {
          enabled,
          hour_utc: d?.hour_utc ?? 2,
          auto_apply_topics: d?.auto_apply_topics === true,
          ...(deps.recallLive
            ? { recall_state: "synced" as const, recall_detail: null, pushed_at: stamp(1) }
            : {
                recall_state: "not_offered" as const,
                recall_detail: NOT_OFFERED,
                pushed_at: null,
              }),
        },
        hot_copy: { enabled: m?.hot_copy?.enabled === true },
        rules_space: deps.recallLive
          ? { state: "synced", detail: null }
          : { state: "not_offered", detail: NOT_OFFERED },
      };
      return json(body);
    },
    "pages.playbookProcedure": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      type PageLite = { id: string; title: string; kind: string; version: number };
      const pg = deps.call<PageLite>(
        "pages.get",
        sub(ctx, `/pages/${ctx.params.id}`, { id: ctx.params.id ?? "" }),
      );
      if (!pg) return notFound();
      const recent = (runs.get(p.company.id) ?? [])
        .filter((r) => r.page_id === pg.id)
        .sort((a, b) => b.created_at.localeCompare(a.created_at));
      const count = (res: RunView["result"]) => recent.filter((r) => r.result === res).length;
      const body: Procedure = {
        page_id: pg.id,
        procedure:
          pg.kind === "sop"
            ? {
                title: pg.title,
                step_count: 0,
                page_version: pg.version,
                ...(deps.recallLive
                  ? { recall_state: "synced" as const, recall_detail: null, synced_at: stamp(5) }
                  : {
                      recall_state: "not_offered" as const,
                      recall_detail: NOT_OFFERED,
                      synced_at: null,
                    }),
              }
            : null,
        runs: {
          total: recent.length,
          succeeded: count("succeeded"),
          failed: count("failed"),
          abandoned: count("abandoned"),
          last_at: recent[0]?.created_at ?? null,
        },
        recent: recent.slice(0, 20),
      };
      return json(body);
    },
  };

  function addProcedureRun(companyId: string, r: FakeProcedureRun) {
    const de = deps.desOf(companyId).find((d) => d.membership_id === r.de_membership_id);
    const list = runs.get(companyId) ?? [];
    list.push({
      id: next(),
      procedure_title: "Playbook",
      page_id: r.page_id,
      by: {
        membership_id: r.de_membership_id,
        name: de?.display_name ?? "DE",
        kind: "digital_employee",
      },
      run_id: null,
      task: r.task,
      result: r.result,
      steps: r.steps ?? [],
      note: r.note ?? null,
      ...bound(),
      created_at: stamp(r.minutes_ago ?? 0),
    });
    runs.set(companyId, list);
  }

  return { handlers, addProcedureRun };
}
